import { Stack } from '@mantine/core';
import numeral from 'numeral';
import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AsyncDisplay, showError, Skeleton, Text, Well } from '@feathr/components';
import { useSessionCache } from '@feathr/extender/hooks';
import { useLocalUrl, useStore, useUsageLimits } from '@feathr/extender/state';

import * as styles from './EmailUsageIndicator.css';

function EmailUsageIndicator(): ReactNode {
  const localUrl = useLocalUrl();
  const { t } = useTranslation();
  const { emailSendLimit } = useUsageLimits();
  const { EmailUsageRecords } = useStore();
  const { getValue } = useSessionCache();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [monthlyUsage, setMonthlyUsage] = useState<number>(0);

  useEffect(() => {
    async function loadData(): Promise<void> {
      setIsLoading(true);
      try {
        const monthlyUsageRecord = await getValue('email_usage_monthly', async () => {
          const now = new Date();
          return EmailUsageRecords.getUsageByMonth({
            target_date: now.toISOString(),
          });
        });
        setMonthlyUsage(monthlyUsageRecord.total);
      } catch (error) {
        showError(error, t);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, []);

  return (
    <Link className={styles.link} to={localUrl('/marketing/email-limits')}>
      <Well className={styles.root}>
        <AsyncDisplay isLoading={isLoading} skeleton={<Skeleton paragraphs={2} width={'100%'} />}>
          <Stack gap={4}>
            <Text.SmallSemibold>{`${numeral(monthlyUsage).format('0,0')} / ${numeral(emailSendLimit).format('0,0')}`}</Text.SmallSemibold>
            <Text.SmallRegular>email sends used this month</Text.SmallRegular>
          </Stack>
        </AsyncDisplay>
      </Well>
    </Link>
  );
}

export default EmailUsageIndicator;
